// hooks/useHorariosPorTemporada.ts
//
// Una sola consulta por entidad, agrupada por temporada en memoria. Evita
// abrir una peticion por cada temporada que se quiera consultar a la vez
// (la general, la seleccionada, la que esta en vigor...).
import { useState, useEffect, useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import type { Horario } from '@/components/types/typeCalendar';
import { DIAS_SEMANA } from '@/components/constants/dias';

export const CLAVE_BASE = '__base__';

const claveDe = (idTemporada: string | null) => idTemporada ?? CLAVE_BASE;

function agrupar(filas: any[]): Record<string, Horario[]> {
  const map: Record<string, Horario[]> = {};
  filas.forEach((f) => {
    const clave = claveDe(f.id_horario_temporada ?? null);
    (map[clave] ??= []).push(f as Horario);
  });
  return map;
}

function validarHorariosLista(lista: any[]): string | null {
  const sorted = [...lista].sort((a, b) =>
    a.hora_inicio.localeCompare(b.hora_inicio)
  );

  for (let i = 0; i < sorted.length; i++) {
    const { hora_inicio, hora_fin } = sorted[i];

    if (!hora_inicio || !hora_fin) return 'Falta hora inicio/fin';
    if (hora_fin <= hora_inicio) return 'La hora de fin debe ser mayor';

    if (i < sorted.length - 1 && hora_fin > sorted[i + 1].hora_inicio) {
      return 'Los horarios se solapan';
    }
  }

  return null;
}

// ============================================================
// TRABAJADOR
// ============================================================
export function useHorariosTrabajadorTodos(
  idUsuario: string | null,
  idEmpresa: string
) {
  const [porTemporada, setPorTemporada] = useState<Record<string, Horario[]>>(
    {}
  );
  const [cargado, setCargado] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchTodos = useCallback(async () => {
    if (!idUsuario || !idEmpresa) {
      setPorTemporada({});
      // Trabajador nuevo: no hay horarios que traer, pero la carga esta
      // resuelta. Si no, quien espere a `cargado` se queda colgado.
      setCargado(!!idEmpresa && !idUsuario);
      return {};
    }

    const { data, error } = await supabase
      .from('vista_horario_trabajador')
      .select('id_horario, dia_semana, hora_inicio, hora_fin, id_horario_temporada')
      .eq('id_usuario', idUsuario)
      .eq('id_empresa', idEmpresa);

    if (error) {
      Sentry.captureException(error, { extra: { idUsuario, idEmpresa } });
      toast.error('Error al cargar horarios del trabajador');
      return {};
    }

    const agrupados = agrupar(data ?? []);
    setPorTemporada(agrupados);
    setCargado(true);
    return agrupados;
  }, [idUsuario, idEmpresa]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  const franjasDe = useCallback(
    (idTemporada: string | null) => porTemporada[claveDe(idTemporada)] ?? [],
    [porTemporada]
  );

  const temporadasConHorario = useMemo(
    () => new Set(Object.keys(porTemporada).filter((k) => k !== CLAVE_BASE)),
    [porTemporada]
  );

  const guardar = async (
    horariosPorDia: Record<string, any[]>,
    usuarioSistemaId: string,
    idUsuarioTrabajador: string | null,
    idTemporada: string | null
  ) => {
    if (!idUsuarioTrabajador || !idEmpresa)
      return { ok: false, error: 'Faltan IDs' };

    setSaving(true);

    try {
      for (const [dia, lista] of Object.entries(horariosPorDia)) {
        const err = validarHorariosLista(lista);
        if (err) {
          toast.error(`Error en ${dia}: ${err}`);
          return { ok: false, error: err };
        }
      }

      let del = supabase
        .from('horario_trabajador')
        .delete()
        .eq('id_usuario', idUsuarioTrabajador)
        .eq('id_empresa', idEmpresa);

      del = idTemporada
        ? del.eq('id_horario_temporada', idTemporada)
        : del.is('id_horario_temporada', null);

      const { error: errorDelete } = await del;
      if (errorDelete) throw errorDelete;

      const payload = Object.values(horariosPorDia)
        .flat()
        .map((e: any) => ({
          id_usuario: idUsuarioTrabajador,
          id_empresa: idEmpresa,
          id_horario_temporada: idTemporada,
          dia_semana: e.dia_semana,
          hora_inicio: e.hora_inicio,
          hora_fin: e.hora_fin,
          usuario_creacion: usuarioSistemaId,
        }));

      if (payload.length > 0) {
        const { error: errorInsert } = await supabase
          .from('horario_trabajador')
          .insert(payload);
        if (errorInsert) throw errorInsert;
      }

      await fetchTodos();
      return { ok: true };
    } catch (error) {
      Sentry.captureException(error, {
        extra: { idUsuarioTrabajador, idEmpresa, idTemporada },
      });
      toast.error('No se pudieron guardar los horarios del trabajador');
      return { ok: false, error };
    } finally {
      setSaving(false);
    }
  };

  return {
    porTemporada,
    franjasDe,
    temporadasConHorario,
    cargado,
    saving,
    refrescar: fetchTodos,
    guardar,
  };
}

// ============================================================
// EMPRESA
// ============================================================
export function useHorariosEmpresaTodos(idEmpresa: string) {
  const [porTemporada, setPorTemporada] = useState<Record<string, Horario[]>>(
    {}
  );
  const [cargado, setCargado] = useState(false);

  const fetchTodos = useCallback(async () => {
    if (!idEmpresa) return {};

    const { data, error } = await supabase
      .from('vista_horario_empresa')
      .select('id_horario, dia_semana, hora_inicio, hora_fin, id_horario_temporada')
      .eq('id_empresa', idEmpresa);

    if (error) {
      Sentry.captureException(error, { extra: { idEmpresa } });
      toast.error('Error al cargar horarios de la empresa');
      return {};
    }

    const agrupados = agrupar(data ?? []);
    setPorTemporada(agrupados);
    setCargado(true);
    return agrupados;
  }, [idEmpresa]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  const franjasDe = useCallback(
    (idTemporada: string | null) => porTemporada[claveDe(idTemporada)] ?? [],
    [porTemporada]
  );

  return { porTemporada, franjasDe, cargado, refrescar: fetchTodos };
}

export { DIAS_SEMANA };